import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { thinkTankAPI } from '../../services/api';
import { thinkTankCache } from '../../utils/cache';
import { errorHandler } from '../../utils/errorHandler';
import { useLoadingTimeout } from '../../hooks/useLoadingTimeout';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import Footer from '../../components/common/Footer/Footer';
import './ThinkTank.css';

const ThinkTank = () => {
    const [projectLead, setProjectLead] = useState(null);
    const [members, setMembers] = useState([]);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState('');
    const { currentUser, hasRole } = useAuth();
    const { loading, timeoutReached, startLoading, stopLoading } = useLoadingTimeout(true);
    const initialized = useRef(false);

    useEffect(() => {
        let mounted = true;

        const initializePage = async () => {
            if (initialized.current) return;
            initialized.current = true;

            try {
                startLoading();
                setError(null);

                // Fetch project lead
                const cachedLead = thinkTankCache.get('lead');
                if (cachedLead) {
                    if (mounted) {
                        setProjectLead(cachedLead);
                        setSelectedLeadId(cachedLead.id);
                        stopLoading();
                        return;
                    }
                }

                try {
                    const response = await thinkTankAPI.getThinkTankLead();
                    if (!mounted) return;

                    if (response) {
                        const leadData = response.lead;
                        const lead = {
                            id: leadData.id,
                            name: leadData.name,
                            title: leadData.role || leadData.title,
                            bio: leadData.biography || leadData.bio,
                            picture: leadData.picture
                        };
                        
                        thinkTankCache.set('lead', lead);
                        setProjectLead(lead);
                        setSelectedLeadId(leadData.id);
                    } else {
                        setError('No project lead data available');
                    }
                } catch (error) {
                    if (mounted) {
                        setError('Failed to load project lead. Please try again later.');
                    }
                }

                // // Fetch members if admin
                // if (mounted && hasRole('admin')) {
                //     try {
                //         const membersResponse = await userAPI.getAllMembers();
                //         if (membersResponse.data) {
                //             setMembers(membersResponse.data);
                //         }
                //     } catch (error) {
                //     }
                // }
            } catch (error) {
                if (mounted) {
                    setError(errorHandler.processError(error).message);
                }
            } finally {
                if (mounted) {
                    stopLoading();
                }
            }
        };

        initializePage();

        return () => {
            mounted = false;
        };
    }, [currentUser, hasRole, startLoading, stopLoading]);

    const handleLeadChange = async (memberId) => {
        try {
            startLoading();
            setError(null);

            await thinkTankAPI.setThinkTankLead(memberId);
            setSelectedLeadId(memberId);
            
            const selectedMember = members.find(member => member.id === memberId);
            if (selectedMember) {
                const newLead = {
                    id: selectedMember.id,
                    name: selectedMember.name,
                    title: selectedMember.role || selectedMember.title,
                    bio: selectedMember.biography || selectedMember.bio,
                    picture: selectedMember.picture
                };
                
                thinkTankCache.set('lead', newLead);
                setProjectLead(newLead);
            }
            
            setIsEditing(false);
        } catch (error) {
            setError(errorHandler.processError(error).message);
        } finally {
            stopLoading();
        }
    };

    const handleJoinClick = async () => {
        if (!currentUser) {
            return;
        }

        try {
            startLoading();
            setError(null);

            await thinkTankAPI.join({
                joinDate: new Date().toISOString()
            });
        } catch (error) {
            setError(errorHandler.processError(error).message);
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        return () => {
            thinkTankCache.delete('lead');
        };
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }
    if (timeoutReached) {
        return <ErrorMessage message="The page is taking longer than expected to load. Please try refreshing." />;
    }
    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <div className="think-tank-page">
            <div className="think-tank-content">
                <section className="project-overview">
                    <h2>Think Tank</h2>
                    <p>
                        The Think Tank is a collaborative initiative that brings together experts, 
                        innovators, and thought leaders to address pressing challenges and develop 
                        innovative solutions. Through research, analysis, and strategic planning, 
                        we aim to create lasting impact in our community and beyond.
                    </p>
                </section>

                <section className="project-lead">
                    <div className="section-header">
                        <h2>Project Lead</h2>
                        {hasRole('admin') && (
                            <div className="admin-actions">
                                {isEditing ? (
                                    <div className="lead-selection">
                                        <select
                                            value={selectedLeadId}
                                            onChange={(e) => setSelectedLeadId(e.target.value)}
                                            className="lead-select"
                                        >
                                            <option value="">Select a project lead</option>
                                            {members.map(member => (
                                                <option key={member.id} value={member.id}>
                                                    {member.name} - {member.title}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="edit-actions">
                                            <button 
                                                className="save-btn"
                                                onClick={() => handleLeadChange(selectedLeadId)}
                                            >
                                                Save
                                            </button>
                                            <button 
                                                className="cancel-btn"
                                                onClick={() => setIsEditing(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button 
                                        className="edit-btn"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        {projectLead ? 'Change Lead' : 'Set Lead'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {projectLead ? (
                        <div className="lead-profile">
                            <div className="lead-image">
                                <img 
                                    src={projectLead.picture || '/images/default-profile.png'} 
                                    alt={projectLead.name}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/images/default-profile.png';
                                    }}
                                />
                            </div>
                            <div className="lead-info">
                                <h3>{projectLead.name}</h3>
                                <p className="title">{projectLead.title}</p>
                                <p className="bio">{projectLead.bio}</p>
                            </div>
                        </div>
                    ) : (
                        <p>No project lead assigned yet.</p>
                    )}
                </section>

                <section className="project-explanation">
                    <h2>About Our Think Tank</h2>
                    <p>
                        The FOMEJE Think Tank is dedicated to addressing key challenges facing Mediterranean youth 
                        through comprehensive research, strategic analysis, and innovative solutions. Our interdisciplinary 
                        approach brings together experts from diverse backgrounds to develop actionable insights and policy 
                        recommendations that can create meaningful change.
                    </p>
                    
                    <div className="key-points">
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-search"></i>
                                </div>
                                <h3>Research Focus</h3>
                            </div>
                            <p>
                                We conduct in-depth studies on youth employment, education accessibility, 
                                and social mobility across Mediterranean countries, identifying patterns and opportunities.
                            </p>
                        </div>
                        
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-lightbulb"></i>
                                </div>
                                <h3>Innovation</h3>
                            </div>
                            <p>
                                We explore emerging technologies and novel approaches to traditional problems,
                                developing fresh solutions that can be implemented at various scales.
                            </p>
                        </div>
                        
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-hands-helping"></i>
                                </div>
                                <h3>Collaboration</h3>
                            </div>
                            <p>
                                We foster partnerships with universities, governments, and NGOs to
                                ensure our research translates into real-world impact and policy changes.
                            </p>
                        </div>
                        
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-chart-line"></i>
                                </div>
                                <h3>Economic Development</h3>
                            </div>
                            <p>
                                We analyze economic trends and develop strategies to boost youth entrepreneurship,
                                workforce development, and sustainable economic growth in the region.
                            </p>
                        </div>
                        
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-globe-africa"></i>
                                </div>
                                <h3>Cultural Exchange</h3>
                            </div>
                            <p>
                                We promote cross-cultural understanding and dialogue among Mediterranean 
                                youth, fostering a shared identity while celebrating diverse heritages.
                            </p>
                        </div>
                        
                        <div className="key-point">
                            <div className="key-point-header">
                                <div className="key-point-icon">
                                    <i className="fas fa-leaf"></i>
                                </div>
                                <h3>Sustainability</h3>
                            </div>
                            <p>
                                We address environmental challenges affecting the Mediterranean region,
                                developing youth-led initiatives for climate resilience and sustainable development.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="join-section">
                    <h2>Join the Think Tank</h2>
                    <p>
                        Are you interested in contributing to our Think Tank? Join us to collaborate 
                        with other experts and make a difference in our community.
                    </p>
                    {/* <button 
                        className="join-button"
                        onClick={handleJoinClick}
                        disabled={!currentUser}
                    >
                        {currentUser ? 'Join Now' : 'Sign in to Join'}
                    </button> */}
                </section>
            </div>
            
            <Footer />
        </div>
    );
};

export default ThinkTank;